// External Imports
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import Helmet from 'react-helmet';
import PropTypes from 'prop-types';
import React, { Component } from 'react';

// Internal Imports
import { actionArticleLinkClicked, actionArticleVideoStarted } from '../../actions/index.js';
import { CampaignsTracker } from '../Tracking/CampaignsTracker.jsx';
import { CONTENT_ITEM, LANGUAGES } from '../../config/types/prop.js';
import { ContentScriptEmbedHandler } from './ContentScriptEmbedHandler.jsx';
import { createGetComponentByContentItem } from '../../selectors/createGetComponent.js';
import { createGetContentItemBySlug } from '../../selectors/createGetContentItem.js';
import { createGetEditionPath } from '../../selectors/createGetEditionPath.js';
import { getContentType } from '../../selectors/getMatch.js';
import { getEdition } from '../../selectors/getEdition.js';
import { getIsInProductMagazine } from '../../selectors/getIsInProductMagazine.js';
import { getMetaImageDimensions } from '../../utils/metadata.js';
import { getRelatedContentItemsByItem } from '../../selectors/relatedItemSelectors.js';
import { HeaderLinks } from '../HeaderLinks/HeaderLinks.jsx';
import { isAbsolute } from '../../utils/url.js';
import { Meta } from '../Meta/Meta.jsx';
import { options } from '../../config/reactRedux.js';
import { removeLinks } from '../../utils/removeLinks.js';
import { setupLazyLoadEmbeds } from '../../utils/lazyLoadEmbeds.js';
import { SINGLE } from '../../config/types/content/component.js';
import { WithLoader } from '../Hoc/WithLoader/WithLoader.jsx';
import { GlobalYoutubeLazyLoadStyle } from '../../utils/youtube.styles.js';
import {
  getIsActiveContentItemLoading,
  getIsActiveContentItemLoadingRelated,
} from '../../selectors/getIsActiveContentItemLoading.js';
// import { AbTester } from '../../components/AbTester/AbTester.jsx';

// Component Definition
class ContentItem extends Component {
  constructor(props) {
    super(props);

    this.contentContainerRef = React.createRef();
    this.lazyLoadEmbedsTearDown = () => {};
    this.trackableLinksTearDown = () => {};
  }

  componentDidMount() {
    this.setupLazyLoadEmbeds();
    this.setupTrackableLinks();
  }

  componentDidUpdate() {
    this.setupLazyLoadEmbeds();
    this.setupTrackableLinks();
  }

  componentWillUnmount() {
    this.lazyLoadEmbedsTearDown();
    this.trackableLinksTearDown();
  }

  setupLazyLoadEmbeds() {
    this.lazyLoadEmbedsTearDown();

    const { contentLazyLoadEmbeds, onVideoStarted } = this.props;
    const container = this.contentContainerRef.current;
    if (Object.keys(contentLazyLoadEmbeds).length > 0) {
      this.lazyLoadEmbedsTearDown = setupLazyLoadEmbeds(
        container,
        contentLazyLoadEmbeds,
        onVideoStarted,
      ).tearDown;
    }
  }

  setupTrackableLinks() {
    this.trackableLinksTearDown();

    const { createPath, isInProductMagazine, history, onLinkClicked } = this.props;
    const container = this.contentContainerRef.current;
    const linksList = [...container.querySelectorAll('a')];

    const inProductNavigation = (clickEvent) => {
      const link = createPath(clickEvent.currentTarget.href);
      if (isAbsolute(link)) {
        clickEvent.preventDefault();
      } else {
        clickEvent.preventDefault();
        history.push(link);
      }
    };

    const trackClickEvent = (clickEvent) => {
      const isTargetAnAnchorTag = clickEvent.target.localName === 'a';
      // Older articles wrap their anchor text within span tags whenever there is a particular
      // style applied to the text. The following condition takes that into consideration
      const currentTarget = isTargetAnAnchorTag
        ? clickEvent.target
        : clickEvent.target.parentElement;
      const { href, textContent } = currentTarget;
      onLinkClicked({
        text: textContent,
        url: href,
      });
    };

    const clickEventListener = (clickEvent) => {
      trackClickEvent(clickEvent);
      if (isInProductMagazine) {
        inProductNavigation(clickEvent);
      }
    };

    const auxClickEventListener = (clickEvent) => {
      // tracks only mouse wheel clicks
      if (clickEvent.button === 1) {
        return clickEventListener(clickEvent);
      }

      return false;
    };

    for (const linkElement of linksList) {
      linkElement.addEventListener('click', clickEventListener);
      linkElement.addEventListener('auxclick', auxClickEventListener);
    }

    this.trackableLinksTearDown = () => {
      for (const linkElement of linksList) {
        linkElement.removeEventListener('click', clickEventListener);
        linkElement.removeEventListener('auxclick', auxClickEventListener);
      }
    };
  }

  buildImageMetaElements() {
    const { item } = this.props;
    const metaList = item.metadata.headerMeta;
    const dimensions = getMetaImageDimensions(metaList);
    return (
      dimensions && (
        <Helmet>
          <meta property="og:image:height" content={dimensions.height} />
          <meta property="og:image:width" content={dimensions.width} />
        </Helmet>
      )
    );
  }

  render() {
    const {
      component: ContentItemComponent,
      contentFiltered,
      contentLazyLoadEmbeds,
      contentScripts,
      currentEdition,
      isInProductMagazine,
      isLoadingRelated,
      item,
      related,
    } = this.props;

    const {
      metadata: {
        canonicalUrl,
        coauthors: [author],
        headerMeta,
        headerTitle,
        languageHreflang,
        tracking: { shortlinkCode },
      },
    } = item;

    return (
      <div>
        {headerMeta && <Meta metaTags={headerMeta} title={headerTitle} />}
        {this.buildImageMetaElements()}
        <Helmet>
          <link rel="author" href={author.relativePath} />
        </Helmet>
        {/* {!isNode() && !abTester.wasQueried && <AbTester
          experimentGroup="test"
          experimentTarget="experiment_cro_353_mag_banner"
          magazineEditionLanguage={currentEdition}
        />} */}
        {Object.keys(contentLazyLoadEmbeds).length > 0 && <GlobalYoutubeLazyLoadStyle />}
        <CampaignsTracker shortlinkCode={shortlinkCode} />
        <ContentScriptEmbedHandler contentScripts={contentScripts} />
        <HeaderLinks canonicalUrl={canonicalUrl} languageHreflang={languageHreflang} />
        <ContentItemComponent
          item={item}
          contentFiltered={contentFiltered}
          related={related}
          currentEdition={currentEdition}
          contentContainerRef={this.contentContainerRef}
          isInProductMagazine={isInProductMagazine}
          isLoadingRelated={isLoadingRelated}
        />
      </div>
    );
  }
}

ContentItem.propTypes = {
  // abTester: PropTypes.shape({
  //   isLoading: PropTypes.bool.isRequired,
  //   isInExperimentGroup: PropTypes.bool.isRequired,
  //   wasQueried: PropTypes.bool.isRequired,
  // }).isRequired,
  component: PropTypes.elementType.isRequired,
  contentFiltered: PropTypes.string.isRequired,
  contentLazyLoadEmbeds: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types -- dynamically built
  contentScripts: PropTypes.arrayOf(PropTypes.string).isRequired,
  createPath: PropTypes.func.isRequired,
  currentEdition: LANGUAGES.isRequired,
  history: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types -- use a TypeScript type eventually
  isInProductMagazine: PropTypes.bool.isRequired,
  isLoadingRelated: PropTypes.bool.isRequired,
  item: PropTypes.shape(CONTENT_ITEM).isRequired,
  match: PropTypes.shape({
    url: PropTypes.string.isRequired,
  }).isRequired,
  onLinkClicked: PropTypes.func.isRequired,
  onVideoStarted: PropTypes.func.isRequired,
  related: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types -- unsure of the object shape
};

const mapStateToProps = (state, props) => {
  // const abTester = get('abTester', state);
  const contentType = getContentType(state);
  const getItem = createGetContentItemBySlug(contentType);
  const item = getItem(state, props);
  const getComponent = createGetComponentByContentItem(item, SINGLE);
  const component = getComponent(state);
  const loading = getIsActiveContentItemLoading(state) || !component;
  const related = getRelatedContentItemsByItem(state, item);
  const isInProductMagazine = getIsInProductMagazine(state);
  const { postProcessedData = {} } = item || {};
  const filterLinksIfNeeded = isInProductMagazine ? removeLinks : (content) => content;
  const contentFiltered = filterLinksIfNeeded(postProcessedData.filteredContent?.content || '');
  const createPath = (path) => createGetEditionPath(path)(state);

  return {
    component,
    contentFiltered,
    contentLazyLoadEmbeds: postProcessedData.lazyLoadEmbeds,
    contentScripts: postProcessedData.scripts,
    createPath,
    currentEdition: getEdition(state),
    isInProductMagazine,
    isLoadingRelated: getIsActiveContentItemLoadingRelated(state),
    item,
    loading,
    related,
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      onLinkClicked: actionArticleLinkClicked,
      onVideoStarted: actionArticleVideoStarted,
    },
    dispatch,
  );

const withRedux = connect(mapStateToProps, mapDispatchToProps, undefined, options);
const ContentItemHoc = withRouter(withRedux(WithLoader(ContentItem)));

// Module Exports
export { ContentItemHoc as ContentItem };
